import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect } from "react";
import {
  Briefcase, ShieldCheck, FileCheck2, Heart, GraduationCap,
  Phone, Image as ImageIcon, ChevronRight, Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function WorkerSignupPage() {
  const navigate = useNavigate();

  useEffect(() => { document.title = "Become a support worker · BTR Health"; }, []);

  const readyItems = [
    { icon: ImageIcon,     text: "A profile photo and a short bio about yourself" },
    { icon: Phone,         text: "A mobile number we can reach you on" },
    { icon: GraduationCap, text: "Any relevant qualifications for the work you'd like to offer" },
    { icon: ShieldCheck,   text: "An NDIS Worker Screening Check (or we'll help you get one)" },
    { icon: Heart,         text: "A Working with Children Check if you'll work with under-18s" },
    { icon: FileCheck2,    text: "An ABN if you already have one (optional)" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="bg-white/70 backdrop-blur border-b border-slate-100">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between max-w-5xl">
          <Link to="/" className="font-bold text-slate-900 text-lg tracking-tight" data-testid="brand-home">BTR Health</Link>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-3xl">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#87CEEB]/15 text-[#3F7A99] text-[11px] font-semibold uppercase tracking-wider mb-4">
            <Briefcase className="w-3 h-3" /> Join BTR Health
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-3" data-testid="worker-intro-heading">
            Become a support worker
          </h1>
          <p className="text-base text-slate-600 leading-relaxed max-w-2xl">
            Register your profile with BTR Health and we'll get in touch if a participant wants to book you.
            The whole thing takes about 3 minutes.
          </p>
        </motion.div>

        {/* Handy things to have ready */}
        <motion.section
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="mt-8 bg-white rounded-2xl border border-slate-100 p-6 sm:p-7"
          data-testid="worker-intro-requirements-section"
        >
          <div className="flex items-start gap-3 mb-5">
            <div className="shrink-0 w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Handy things to have ready</h2>
              <p className="text-sm text-slate-500">Not all required — submit what you have and we'll help with the rest.</p>
            </div>
          </div>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3 ml-1">
            {readyItems.map((it, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700 leading-relaxed">
                <it.icon className="w-4 h-4 text-[#5BA3C0] mt-0.5 shrink-0" />
                <span>{it.text}</span>
              </li>
            ))}
          </ul>
        </motion.section>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18 }}
          className="mt-8"
        >
          <Button
            onClick={() => navigate("/worker-signup/create")}
            className="h-12 px-7 bg-[#87CEEB] hover:bg-[#6BB8DB] text-white font-bold rounded-full shadow-md shadow-[#87CEEB]/30 text-base"
            data-testid="worker-intro-get-started"
          >
            Register your profile <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
          <p className="text-[11px] text-slate-400 mt-4">
            By continuing you agree to our{" "}
            <Link to="/terms" className="underline hover:text-slate-600">Terms</Link>{" "}and{" "}
            <Link to="/privacy" className="underline hover:text-slate-600">Privacy Policy</Link>.
          </p>
        </motion.div>
      </main>
    </div>
  );
}
